import {
  findUnrenderableTables,
  meetingNotesMarkdownToDoc,
  type UnrenderableTable,
  type UnrenderableTableReason,
} from '@kiao-voice/core/meeting-notes'
import { renderMeetingNotesHtml } from '@kiao-voice/ui-kit/meeting-notes-render'

/**
 * The pure half of the template preview: markdown in, "what the drawer should
 * show" out. No React, no DOM, no i18n — so the rules that decide whether a
 * template is savable are testable without mounting a drawer.
 *
 * Why a preview exists at all: the built-in 一般會議紀錄 carried an aligned-text
 * fake table for a month because the only editor was a bare textarea. The API
 * now refuses that markdown (PR #959), but a 422 on save is a worse way to find
 * out than seeing the paragraph-where-a-table-should-be while you paste.
 */
export type TemplateProblem = UnrenderableTable

export interface TemplatePreview {
  /** Exactly what `/app` would render for this template — same renderer, same CSS. */
  html: string
  problems: TemplateProblem[]
  /** Nothing typed yet: the preview shows a placeholder, not an empty document. */
  isEmpty: boolean
}

/**
 * i18n keys for the two ways a table fails to render. Exhaustive by type, so a
 * new reason in `@kiao-voice/core` breaks the build here rather than silently
 * rendering a blank explanation.
 */
export const TEMPLATE_PROBLEM_REASON_KEYS: Record<UnrenderableTableReason, string> = {
  'whitespace-columns': 'notesTemplatePreview.reasonWhitespaceColumns',
  'missing-divider': 'notesTemplatePreview.reasonMissingDivider',
}

/** The problems on their own — for the Save button, which needs the verdict but
 *  not the rendered HTML. */
export function findTemplateProblems(markdown: string): TemplateProblem[] {
  return findUnrenderableTables(markdown)
}

/**
 * The save policy, named rather than inlined as `problems.length === 0`: it is
 * the one rule the drawer, its tests and any future writer have to agree on.
 *
 * Deliberately all-or-nothing. A template holding one fake table is a template
 * whose every generated note will hold one fake table, so there is no "save it
 * anyway" that leaves the user better off.
 */
export function canSaveTemplate(problems: readonly TemplateProblem[]): boolean {
  return problems.length === 0
}

export function buildTemplatePreview(markdown: string): TemplatePreview {
  const trimmed = markdown.trim()
  if (trimmed === '') return { html: '', problems: [], isEmpty: true }
  return {
    html: renderMeetingNotesHtml(meetingNotesMarkdownToDoc(markdown)),
    problems: findUnrenderableTables(markdown),
    isEmpty: false,
  }
}
